import { Avatar, Button, Group, Stack, Text, FileButton, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCamera, IconTrash } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { profileApi } from '@/api/profile'
import { useAuthStore } from '@/store/auth'
import { formatApiError } from '@/api/errors'

const MAX_BYTES = 2 * 1024 * 1024
const ALLOWED_MIMES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']

interface Props {
  initials: string
  avatarUrl?: string | null
  /** false → backend sem bucket configurado; esconde botoes (G1). */
  supported: boolean
}

export function AvatarUpload({ initials, avatarUrl, supported }: Props) {
  const qc = useQueryClient()
  const { refreshUser } = useAuthStore.getState()
  const fileResetRef = useRef<() => void>(null)
  // Preview imediato via URL.createObjectURL enquanto PUT roda
  const [localPreview, setLocalPreview] = useState<string | null>(null)

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!ALLOWED_MIMES.includes(file.type)) {
        throw new Error('Tipo de arquivo não permitido. Use PNG, JPG ou WebP.')
      }
      if (file.size > MAX_BYTES) {
        throw new Error('Arquivo muito grande. Limite: 2 MB.')
      }
      const presign = await profileApi.avatarPresign(file.name, file.type, file.size)
      await fetch(presign.presigned_put_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      })
      return profileApi.avatarConfirm(presign.s3_key)
    },
    onMutate: (file) => {
      setLocalPreview(URL.createObjectURL(file))
    },
    onSuccess: () => {
      notifications.show({ color: 'teal', title: 'Foto atualizada', message: 'Avatar trocado com sucesso.' })
      qc.invalidateQueries({ queryKey: ['me'] })
      refreshUser()
      fileResetRef.current?.()
      // Mantem preview local ate o /me retornar a URL nova; depois revoga
      setTimeout(() => {
        if (localPreview) URL.revokeObjectURL(localPreview)
        setLocalPreview(null)
      }, 3000)
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', title: 'Falha no upload', message: formatApiError(err) })
      if (localPreview) URL.revokeObjectURL(localPreview)
      setLocalPreview(null)
      fileResetRef.current?.()
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => profileApi.avatarRemove(),
    onSuccess: () => {
      notifications.show({ color: 'orange', title: 'Foto removida', message: 'Voltamos a usar suas iniciais.' })
      qc.invalidateQueries({ queryKey: ['me'] })
      refreshUser()
    },
    onError: (err) => {
      notifications.show({ color: 'red', title: 'Erro ao remover', message: formatApiError(err) })
    },
  })

  const displayUrl = localPreview ?? avatarUrl ?? undefined

  return (
    <Stack gap={8} align="center">
      <Avatar
        src={displayUrl}
        size={96}
        radius="xl"
        color="inovareTeal"
        variant="filled"
        style={{ boxShadow: 'var(--ai-shadow-md)' }}
      >
        <Text size="xl" fw={700}>{initials}</Text>
      </Avatar>
      {supported && (
        <Group gap={4} justify="center">
          <FileButton
            onChange={(file) => { if (file) uploadMutation.mutate(file) }}
            accept={ALLOWED_MIMES.join(',')}
            resetRef={fileResetRef}
            disabled={uploadMutation.isPending}
          >
            {(props) => (
              <Button
                {...props}
                size="xs" variant="light"
                leftSection={<IconCamera size={14} />}
                loading={uploadMutation.isPending}
              >
                {avatarUrl ? 'Trocar' : 'Adicionar foto'}
              </Button>
            )}
          </FileButton>
          {avatarUrl && (
            <Tooltip label="Remover foto">
              <Button
                size="xs" variant="subtle" color="red"
                leftSection={<IconTrash size={14} />}
                loading={removeMutation.isPending}
                onClick={() => removeMutation.mutate()}
              >
                Remover
              </Button>
            </Tooltip>
          )}
        </Group>
      )}
      {!supported && (
        <Text size="xs" c="dimmed" ta="center">
          Upload de foto indisponível — bucket S3 não configurado.
        </Text>
      )}
    </Stack>
  )
}
